import { Manifest, RecentObservedData } from '../types';
import { getApiServer, appendAuthToken } from '../config';
import { TierAccessService, TileAccessDenial } from './TierAccessService';

export class ObservedBinaryService {
  public static lastAccessDenial: TileAccessDenial | null = null;
  private static cache: Map<string, RecentObservedData> = new Map();

  public static async getObservedForPoint(
    lat: number,
    lon: number,
    manifest?: Manifest
  ): Promise<RecentObservedData | null> {
    const zoom = manifest?.observed_point_tiles?.zoom ?? 5;
    const n = 1 << zoom;

    const tileX = Math.floor(((lon + 180) / 360) * n);
    const latRad = (lat * Math.PI) / 180;
    const mercN = Math.log(Math.tan(latRad) + 1 / Math.cos(latRad));
    const tileY = Math.floor((1 - mercN / Math.PI) / 2 * n);

    // CONUS tile boundary check
    if (tileX < 4 || tileX > 10 || tileY < 10 || tileY > 13) {
      return {
        rain1hInches: 0,
        rain24hInches: 0,
        rain72hInches: 0,
        snow24hInches: 0,
        snow72hInches: 0,
        snowBaseDepthInches: 0,
        lightningStrikes50mi: 0,
        hasLightning: false
      };
    }

    const worldDim = n * 64;
    const px = Math.floor(((lon + 180) / 360) * worldDim) % 64;
    const py = Math.floor((1 - mercN / Math.PI) / 2 * worldDim) % 64;
    const cellIdx = py * 64 + px;

    const cacheKey = `${tileX}_${tileY}:${cellIdx}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    const apiServer = getApiServer(manifest);
    const tileBase = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const tileSubdir = manifest?.observed_point_tiles?.tile_path || 'observed/observed_z5';
    const cleanServer = apiServer.replace(/\/$/, '');

    const tileUrl = `${cleanServer}/${tileBase}/${tileSubdir}/${tileX}_${tileY}.bin`.replace(/\/+/g, '/').replace(':/', '://');
    const headerBytes = manifest?.observed_point_tiles?.header_bytes ?? 48;
    const cellBytes = manifest?.observed_point_tiles?.cell_bytes ?? 14;

    const startByte = headerBytes + cellIdx * cellBytes;
    const endByte = startByte + cellBytes - 1;

    try {
      const authenticatedUrl = appendAuthToken(tileUrl);
      const resp = await fetch(authenticatedUrl, {
        credentials: 'include',
        headers: {
          Range: `bytes=${startByte}-${endByte}`
        }
      });

      if (!resp.ok && resp.status !== 206) {
        const denial = TierAccessService.parseTileAccessDenial(resp, 'pro');
        if (denial) {
          this.lastAccessDenial = denial;
        }
        return null;
      }
      this.lastAccessDenial = null;

      const rawBuffer = await resp.arrayBuffer();
      let cellBuffer: ArrayBuffer;
      if (resp.status === 206) {
        cellBuffer = rawBuffer;
      } else if (rawBuffer.byteLength >= endByte + 1) {
        cellBuffer = rawBuffer.slice(startByte, endByte + 1);
      } else {
        return null;
      }

      if (cellBuffer.byteLength < 14) {
        return null;
      }

      const view = new DataView(cellBuffer);
      const rain1hRaw = view.getInt16(0, true);
      const rain24hRaw = view.getInt16(2, true);
      const rain72hRaw = view.getInt16(4, true);
      const snow24hRaw = view.getInt16(6, true);
      const snow72hRaw = view.getInt16(8, true);
      const snowBaseRaw = view.getInt16(10, true);
      const lightningRaw = view.getInt16(12, true);

      const result: RecentObservedData = {
        rain1hInches: Math.max(0, Number((rain1hRaw / 100.0).toFixed(2))),
        rain24hInches: Math.max(0, Number((rain24hRaw / 100.0).toFixed(2))),
        rain72hInches: Math.max(0, Number((rain72hRaw / 100.0).toFixed(2))),
        snow24hInches: Math.max(0, Number((snow24hRaw / 10.0).toFixed(1))),
        snow72hInches: Math.max(0, Number((snow72hRaw / 10.0).toFixed(1))),
        snowBaseDepthInches: Math.max(0, snowBaseRaw),
        lightningStrikes50mi: Math.max(0, lightningRaw),
        hasLightning: lightningRaw > 0
      };

      this.cache.set(cacheKey, result);
      return result;
    } catch (e) {
      console.warn('[ObservedBinaryService] Failed to load observed point binary tile:', e);
      return null;
    }
  }

  public static clearCache(): void {
    this.cache.clear();
    this.lastAccessDenial = null;
  }
}
